import { ASSET_URL } from "@api";
import { SELECTED_PROJECT } from "@configuration";
import { TranslationContext } from "@contexts";
import { useQueryAppConfiguration } from "@hooks/appConfiguration/useQueryAppConfiguration";
import { useQueryGetUserByKeycloakId } from "@hooks/user/useQueryGetUserByKeycloakId";
import { useScrollToTop } from "@hooks/useScrollToTop";
import { Icon } from "@iconify/react";
import { useKeycloak } from "@react-keycloak/web";
import { memo, useContext, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { handleNonValidImg } from "../../helpers";
import { DropDownMenu } from "../DropDownMenu";
import { AliciaNavLinks } from "./AliciaNavLinks";
import { AllianceNavLinks } from "./AllianceNavLinks";
import { CartIconComp } from "./CartIconComp";
import { dropDownLanguages } from "./helpers";
import { LanguageMenuDropDownButton } from "./LanguageMenuDropDownButton";
import { MainMenuModal } from "./MainMenuModal";
import { R3MydasNavLinks } from "./R3MydasNavLinks";

export const NavBar = memo(() => {
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);

  const { data: configObj, isLoading } = useQueryAppConfiguration();

  const { t } = useContext(TranslationContext);

  const location = useLocation();

  const { scrollToTop } = useScrollToTop();

  const { i18n } = useContext(TranslationContext);

  const [showModal, setShowModal] = useState(false);

  const { keycloak } = useKeycloak();
  const isAuthenticated = keycloak.authenticated;
  const isSuperAdmin = keycloak.hasRealmRole("super-admin");
  const isAdmin = keycloak.hasRealmRole("admin");

  const { data: user } = useQueryGetUserByKeycloakId();

  function changeLanguage(lang) {
    i18n.changeLanguage(lang);
    setShowLanguageMenu(false);
  }

  function checkIfActive(path) {
    return location.pathname === path;
  }

  useEffect(() => {
    document.title = configObj.tabTitle || "-";
  }, [configObj.tabTitle]);

  useEffect(() => {
    const favicon = document.querySelector("link[rel='icon']");
    if (favicon) {
      favicon.href = configObj.favicon
        ? `${ASSET_URL}/${configObj.favicon}`
        : "empty_favicon.png";
    }
  }, [configObj.favicon]);

  const navbarBackgroundColor = configObj.navbarBackgroundColor ?? "#1E3A8A";
  const navbarTextColor = configObj.navbarTextColor ?? "white";

  return isLoading ? null : (
    <header
      className={[
        "NavBar",
        "fixed top-0 left-0",
        "w-screen max-w-screen h-16",
        "grid grid-cols-[max-content_max-content]",
        "justify-between gap-2 items-center",
        "px-4 shadow-md z-[100]",
        "xl:pr-8",
      ].join(" ")}
      style={{ backgroundColor: navbarBackgroundColor }}
      data-testid="navBar"
    >
      <Link
        to="/"
        className="flex items-center gap-2 justify-self-start text-xl font-bold xl:text-2xl xl:gap-4"
        style={{ color: navbarTextColor }}
        onClick={scrollToTop}
      >
        {configObj.appName}

        <img
          src={
            configObj.appLogoSrc ? `${ASSET_URL}/${configObj.appLogoSrc}` : ""
          }
          alt={`${configObj.appName} logo`}
          className="h-8 xl:h-10"
          onError={handleNonValidImg}
          data-testid="appLogo"
        />
      </Link>
      {isAuthenticated ? (
        <>
          <Icon
            icon="heroicons-solid:menu"
            className="text-2xl text-white xl:hidden justify-self-end"
            onClick={() => setShowModal(true)}
          />
          <nav
            className="hidden xl:grid place-items-center grid-flow-col gap-5 justify-self-end"
            data-testid="navBarContent"
          >
            {configObj.hasMultiLanguage && (
              <DropDownMenu
                items={dropDownLanguages}
                onItemClicked={(lang) => changeLanguage(lang)}
                MenuButton={
                  <LanguageMenuDropDownButton
                    textColor={configObj.navbarTextColor}
                  />
                }
                showMenu={showLanguageMenu}
                setShowMenu={setShowLanguageMenu}
              />
            )}
            {(isSuperAdmin || isAdmin) && (
              <Link
                to="/back-office"
                className={`grid grid-cols-[max-content_max-content] gap-0.5 items-center border-none bg-none cursor-pointer font-medium transition-all duration-200 ease-in-out hover:scale-110 hover:text-[#f2987a] focus:text-[#f2987a] focus:outline-none ${
                  checkIfActive("/back-office") ? "text-amber-500" : ""
                }`}
                style={
                  !checkIfActive("/back-office")
                    ? { color: navbarTextColor }
                    : undefined
                }
              >
                {t("backOffice")}
              </Link>
            )}
            {configObj.showAbout && (
              <Link
                to="/about"
                className={`grid grid-cols-[max-content_max-content] gap-0.5 items-center border-none bg-none cursor-pointer font-medium transition-all duration-200 ease-in-out hover:scale-110 hover:text-[#f2987a] focus:text-[#f2987a] focus:outline-none ${
                  checkIfActive("/about") ? "text-amber-500" : ""
                }`}
                style={
                  !checkIfActive("/about")
                    ? { color: navbarTextColor }
                    : undefined
                }
                data-testid="aboutNav"
              >
                {t("about")}
              </Link>
            )}
            {/* ALICIA */}
            {SELECTED_PROJECT === "alicia" && (
              <AliciaNavLinks
                t={t}
                checkIfActive={checkIfActive}
                navbarTextColor={navbarTextColor}
                user={user}
              />
            )}

            {/* ALLIANCE */}
            {SELECTED_PROJECT === "alliance" && (
              <AllianceNavLinks
                t={t}
                checkIfActive={checkIfActive}
                navbarTextColor={navbarTextColor}
              />
            )}
            {SELECTED_PROJECT === "r3-mydas" && (
              <R3MydasNavLinks
                t={t}
                checkIfActive={checkIfActive}
                navbarTextColor={navbarTextColor}
              />
            )}
            {configObj.hasCart && (
              <Link
                to="/cart"
                className={`grid grid-cols-[max-content_max-content] gap-0.5 items-center border-none bg-none cursor-pointer font-medium transition-all duration-200 ease-in-out hover:scale-110 hover:text-[#f2987a] focus:text-[#f2987a] focus:outline-none ${
                  checkIfActive("/cart") ? "text-amber-500" : ""
                }`}
                style={
                  !checkIfActive("/cart")
                    ? { color: navbarTextColor }
                    : undefined
                }
              >
                <CartIconComp />
              </Link>
            )}
            <Link
              to="/my-profile"
              className={`grid place-items-center w-8 h-8 bg-none cursor-pointer font-medium transition-all duration-200 ease-in-out hover:scale-110 hover:text-[#f2987a] focus:text-[#f2987a] focus:outline-none ${
                checkIfActive("/my-profile") ? "text-amber-500" : ""
              } !border-1 border-gray-100 !rounded-full p-1 mt-[5px]`}
              style={
                !checkIfActive("/my-profile")
                  ? { color: navbarTextColor }
                  : undefined
              }
              data-testid="profileNavLink"
            >
              <Icon icon="heroicons-solid:user-circle" className="text-xl" />
            </Link>
          </nav>
        </>
      ) : (
        <button
          onClick={() =>
            keycloak.login({
              redirectUri: `${window.location.origin}/`,
            })
          }
          className="justify-self-end border-none bg-none cursor-pointer font-medium transition-all duration-200 ease-in-out hover:scale-110 hover:text-[#f2987a] focus:text-[#f2987a] focus:outline-none"
          style={{ color: navbarTextColor }}
          data-testid="loginBtn"
        >
          {t("login")}
        </button>
      )}
      {showModal && <MainMenuModal setShowModal={setShowModal} />}
    </header>
  );
});
